import { Button, Space, Table, TableColumnsType, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicationSummary,
	ApplicationTableDataType,
	Pagination,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	displayPreworkStatus,
	firstLetterUpperCase,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import {
	ApplicationPreworkStatus,
	PreworkCompletionStatus,
} from '../../../shared/PreWorkServiceTypes';
import moment from 'moment';
import { InfoCircleFilled } from '@ant-design/icons';
import { DATE_DO_MMM_YYYY } from '../../../shared/Constants';

interface ApplicationSummaryTableProps {
	applications: ApplicationPreworkStatus[] | undefined;
	onEvaluateClick: (application: ApplicationSummary) => void;
	onPageChange: (page: number, pageSize: number) => void;
	pageConfig: PagingConfig;
	pagination: Pagination;
}

export const ApplicationSummaryTable = (
	props: ApplicationSummaryTableProps
) => {
	const {
		applications,
		onEvaluateClick,
		onPageChange,
		pageConfig,
		pagination,
	} = props;
	const { t } = useTranslation();

	const tablePaginationConfig = tablePaginationConfiguration(
		pageConfig,
		pagination,
		onPageChange
	);

	const handleEvaluateClick = (application: ApplicationTableDataType) => {
		onEvaluateClick(application.originalApplication);
	};

	const applicationTableData = applications?.map(
		({
			application,
			preworkCompletionStatus,
			preworkMentorCompletionStatus,
			preworkMentorNextAttemptDate,
		}) => {
			const {
				applicant: { firstName = '', lastName = '', srcmid, gender = '' },
				center: { zone },
			} = application;
			const preworkStatus = t(
				displayPreworkStatus(
					preworkCompletionStatus,
					preworkMentorCompletionStatus
				)
			);
			const tableData: ApplicationTableDataType = {
				key: srcmid,
				fullName: `${firstName} ${lastName}`,
				idCard: srcmid,
				gender,
				zone,
				originalApplication: application,
				preworkStatus,
				originalMentorPreworkStatus: preworkMentorCompletionStatus,
				disableViewAction:
					(preworkMentorNextAttemptDate &&
						moment().isSameOrBefore(preworkMentorNextAttemptDate)) ||
					preworkCompletionStatus !== PreworkCompletionStatus.COMPLETED ||
					!preworkCompletionStatus,
				preworkMentorNextAttemptDate,
			};

			return tableData;
		}
	);

	const baseColumns: TableColumnsType<ApplicationTableDataType> = [
		{
			title: t('fullName'),
			dataIndex: 'fullName',
			key: 'fullName',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('idCard'),
			dataIndex: 'idCard',
			key: 'idCard',
		},
		{
			title: t('gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (__, record, index) => (
				<span key={index}>
					<span className="ml-2">{firstLetterUpperCase(record?.gender)}</span>
				</span>
			),
		},
		{
			title: t('zone'),
			dataIndex: 'zone',
			key: 'zone',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('preworkStatus'),
			dataIndex: 'preworkStatus',
			key: 'preworkStatus',
		},
	];

	baseColumns.push({
		title: t('action'),
		key: 'operation',
		fixed: 'right',
		width: 150,
		render: (__, record) => {
			const resubmitDate = moment(record.preworkMentorNextAttemptDate).format(
				DATE_DO_MMM_YYYY
			);
			const showInfoIcon =
				record.disableViewAction &&
				(record.originalMentorPreworkStatus ===
					PreworkCompletionStatus.INCOMPLETE ||
					record.originalMentorPreworkStatus ===
						PreworkCompletionStatus.PARTIALLY_COMPLETED);
			return (
				<Space size="middle" className="whitespace-nowrap">
					<div className="flex gap-2 justify-center">
						<Button
							type="primary"
							onClick={() => handleEvaluateClick(record)}
							className="w-fit"
							disabled={record.disableViewAction}
						>
							{record.originalMentorPreworkStatus ===
							PreworkCompletionStatus.COMPLETED
								? t('view')
								: t('review')}
						</Button>
						{showInfoIcon && (
							<Tooltip
								className="text-gray-400 text-base"
								title={t('resubmitAfter', { resubmitDate })}
							>
								<InfoCircleFilled className="text-xl" />
							</Tooltip>
						)}
					</div>
				</Space>
			);
		},
	});

	const columns = [...baseColumns];

	return (
		<>
			<Table
				columns={columns}
				dataSource={applicationTableData}
				pagination={tablePaginationConfig}
				scroll={{ x: 'max-content' }}
				rowClassName={() => 'border-b border-gray-300 bg-white'}
				className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
			/>
		</>
	);
};
